import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { useT } from '../i18n';
import { useRefreshable } from '../context/RefreshContext';
import { updateUserAvatar } from '../firebase/userService';
import AvatarLightbox from './AvatarLightbox';
import ActionIcon from './ActionIcon';
import './DesignerProfileHeader.css';
import PersonIcon from './PersonIcon';

// ── CUSTOMER PROFILE HEADER ──────────────────────────────────────────────────
// A customer's version of the designer identity strip: picture, name, and the
// three places they go from here.
//
// Deliberately just those. A customer has no public profile — no bio, styles,
// rate, websites or contacts, because nobody browses to them — so the fields
// that make up a designer's header would all render empty. What is left is
// what a profile is for on this account type: knowing it is yours, and getting
// somewhere from it.
//
// Shares .dph with the designer header rather than inventing a parallel set of
// classes, so the two pages are recognisably the same page.
export default function CustomerProfileHeader() {
  const { user, profile, refreshProfile } = useAuth();
  const { count: basketCount } = useBasket();
  const { t } = useT();
  const navigate = useNavigate();
  const [viewingAvatar, setViewingAvatar] = useState(false);

  useRefreshable(() => refreshProfile());

  const name = profile?.fullName || user?.displayName || user?.email?.split('@')[0] || '';

  // Saved immediately — the lightbox has no Save button, so choosing a picture
  // IS the confirmation. Identical to the designer path on purpose.
  const saveAvatar = async (url) => {
    await updateUserAvatar(user.uid, url);
    await refreshProfile();
  };

  // NO "MY WORKSPACE" LINK. The workspace is rendered on this very page, just
  // below (see Profile.jsx) — a button that scrolls you a few hundred pixels
  // down the page you are already on is not a destination.
  // `icon` names a glyph in ActionIcon — the SAME set the designer header uses,
  // so the two columns stay one control rather than drifting into two.
  const actions = [
    { label: t('md_title'), to: '/my-designers', icon: 'designers' },
    { label: t('ml_title'), to: '/my-liked', icon: 'liked' },
    // The count rides along only when there is one — "My basket 0" states an
    // absence the empty page already explains better.
    { label: t('bsk_title'), to: '/basket', n: basketCount, icon: 'basket' },
  ];

  return (
    <header className="dph dph--own dph--slim">
      <button
        type="button"
        className="dph-avatar dph-avatar--btn"
        onClick={() => setViewingAvatar(true)}
        aria-label={t('avl_open')}
        title={t('avl_open')}
      >
        {profile?.avatarUrl
          ? <img src={profile.avatarUrl} alt="" />
          : <PersonIcon />}
        <span className="dph-avatar-veil" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9a2 2 0 0 1 2-2h1.6a2 2 0 0 0 1.7-.9l.8-1.2a2 2 0 0 1 1.7-.9h2.4a2 2 0 0 1 1.7.9l.8 1.2a2 2 0 0 0 1.7.9H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <circle cx="12" cy="13" r="3.2" />
          </svg>
        </span>
      </button>

      {viewingAvatar && (
        <AvatarLightbox
          src={profile?.avatarUrl || ''}
          name={name}
          editable
          onChange={saveAvatar}
          onClose={() => setViewingAvatar(false)}
        />
      )}

      <div className="dph-ident">
        <h1 className="dph-name">{name}</h1>
        {user?.email && <p className="dph-email">{user.email}</p>}
      </div>

      <div className="dph-actions">
        {actions.map(a => (
          <button key={a.to} className="dph-workspace" onClick={() => navigate(a.to)}>
            <ActionIcon name={a.icon} />
            {a.label}
            {a.n > 0 && <span className="dph-workspace-n">{a.n}</span>}
          </button>
        ))}
      </div>
    </header>
  );
}
